import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { AssignmentSubmissionStatus } from "./useAssignments";
import type { AssignmentCompletionRequirement } from "@/lib/assignmentGating";
import { isAssignmentGateSatisfied, tierGatesProgression } from "@/lib/assignmentGating";

export interface SubmissionWithContext {
  id: string;
  assignment_id: string;
  status: AssignmentSubmissionStatus;
  submitted_at: string;
  instructor_feedback: string | null;
  isLocked?: boolean;
  lockReason?: string;
  assignment: {
    id: string;
    title: string;
    description: string | null;
    required: boolean;
    completion_requirement: AssignmentCompletionRequirement;
    module_id: string;
    module: {
      id: string;
      title: string;
      sequence_order: number;
      course_id: string;
      course: {
        id: string;
        title: string;
        thumbnail_url: string | null;
      };
    };
  };
}

export interface AssignmentWithContext {
  id: string;
  title: string;
  description: string | null;
  required: boolean;
  completion_requirement: AssignmentCompletionRequirement;
  module_id: string;
  isLocked?: boolean;
  lockReason?: string;
  module: {
    id: string;
    title: string;
    sequence_order: number;
    course_id: string;
    course: {
      id: string;
      title: string;
      thumbnail_url: string | null;
    };
  };
}

export interface StudentAssignmentData {
  needsAttention: SubmissionWithContext[];
  awaitingReview: SubmissionWithContext[];
  completed: SubmissionWithContext[];
  notStarted: AssignmentWithContext[];
  totalCount: number;
  needsAttentionCount: number;
}

/**
 * Check if a module is locked based on previous modules' completion.
 * A module is locked if any previous module has a gating assignment whose
 * requirement isn't satisfied for this learner.
 *
 * @param moduleSequenceOrder - The sequence order of the module to check
 * @param allModules - All modules in the course
 * @param gatingAssignmentsByModule - Pre-fetched map of module_id -> assignments that gate progression
 * @param submissionStatusMap - Map of assignment_id -> submission status
 */
function isModuleLockedSync(
  moduleSequenceOrder: number,
  allModules: { id: string; sequence_order: number }[],
  gatingAssignmentsByModule: Map<
    string,
    { id: string; completion_requirement: AssignmentCompletionRequirement }[]
  >,
  submissionStatusMap: Map<string, AssignmentSubmissionStatus>
): { locked: boolean; reason?: string } {
  const minSequenceOrder = allModules.length > 0
    ? Math.min(...allModules.map(m => m.sequence_order))
    : 1;

  if (moduleSequenceOrder <= minSequenceOrder) {
    return { locked: false };
  }

  const previousModules = allModules
    .filter((m) => m.sequence_order < moduleSequenceOrder)
    .sort((a, b) => a.sequence_order - b.sequence_order);

  for (const prevModule of previousModules) {
    const gatingAssignments = gatingAssignmentsByModule.get(prevModule.id) || [];

    for (const a of gatingAssignments) {
      const status = submissionStatusMap.get(a.id) ?? null;
      if (!isAssignmentGateSatisfied(a.completion_requirement, status)) {
        return {
          locked: true,
          reason: "Complete previous module to unlock",
        };
      }
    }
  }

  return { locked: false };
}

export function useStudentAssignments() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["student_assignments", user?.id],
    queryFn: async (): Promise<StudentAssignmentData> => {
      if (!user) {
        return {
          needsAttention: [],
          awaitingReview: [],
          completed: [],
          notStarted: [],
          totalCount: 0,
          needsAttentionCount: 0,
        };
      }

      // Fetch all submissions for this user with assignment context (including sequence_order)
      const { data: submissions, error: submissionsError } = await supabase
        .from("assignment_submissions")
        .select(`
          id,
          assignment_id,
          status,
          submitted_at,
          instructor_feedback,
          assignment:assignments(
            id,
            title,
            description,
            required,
            completion_requirement,
            module_id,
            module:modules(
              id,
              title,
              sequence_order,
              course_id,
              course:courses(id, title, thumbnail_url)
            )
          )
        `)
        .eq("user_id", user.id)
        .order("submitted_at", { ascending: false });

      if (submissionsError) throw submissionsError;

      // Get courses student is enrolled in
      const { data: enrollments, error: enrollmentsError } = await supabase
        .from("course_enrollments")
        .select("course_id")
        .eq("user_id", user.id);

      if (enrollmentsError) throw enrollmentsError;

      const courseIds = enrollments?.map((e) => e.course_id) || [];

      // Get all published assignments in enrolled courses (including sequence_order)
      let allAssignments: AssignmentWithContext[] = [];
      
      if (courseIds.length > 0) {
        const { data: assignmentsData, error: assignmentsError } = await supabase
          .from("assignments")
          .select(`
            id,
            title,
            description,
            required,
            completion_requirement,
            module_id,
            is_published,
            module:modules!inner(
              id,
              title,
              sequence_order,
              course_id,
              course:courses!inner(id, title, thumbnail_url)
            )
          `)
          .eq("is_published", true);

        if (assignmentsError) throw assignmentsError;

        // Filter to only assignments from enrolled courses
        allAssignments = (assignmentsData || [])
          .filter((a: any) => courseIds.includes(a.module?.course_id))
          .map((a: any) => ({
            id: a.id,
            title: a.title,
            description: a.description,
            required: a.required,
            completion_requirement: (a.completion_requirement as AssignmentCompletionRequirement)
              ?? (a.required ? "review_required" : "optional"),
            module_id: a.module_id,
            module: {
              id: a.module.id,
              title: a.module.title,
              sequence_order: a.module.sequence_order,
              course_id: a.module.course_id,
              course: {
                id: a.module.course.id,
                title: a.module.course.title,
                thumbnail_url: a.module.course.thumbnail_url,
              },
            },
          }));
      }

      // Get all modules for enrolled courses to check progression
      const { data: allModulesData } = await supabase
        .from("modules")
        .select("id, sequence_order, course_id")
        .in("course_id", courseIds.length > 0 ? courseIds : ["none"]);

      const modulesByCourse = new Map<string, { id: string; sequence_order: number }[]>();
      const allModuleIds: string[] = [];
      (allModulesData || []).forEach((m: any) => {
        const existing = modulesByCourse.get(m.course_id) || [];
        existing.push({ id: m.id, sequence_order: m.sequence_order });
        modulesByCourse.set(m.course_id, existing);
        allModuleIds.push(m.id);
      });

      // BATCH FETCH: Get all gating assignments (any non-optional tier) for all
      // modules in one query. This replaces the N+1 pattern of querying per module.
      const requiredAssignmentsByModule = new Map<
        string,
        { id: string; completion_requirement: AssignmentCompletionRequirement }[]
      >();
      if (allModuleIds.length > 0) {
        const { data: requiredAssignmentsData } = await supabase
          .from("assignments")
          .select("id, module_id, required, completion_requirement")
          .in("module_id", allModuleIds)
          .eq("is_published", true)
          .neq("completion_requirement", "optional");

        (requiredAssignmentsData || []).forEach((a: any) => {
          const tier: AssignmentCompletionRequirement =
            (a.completion_requirement as AssignmentCompletionRequirement)
              ?? (a.required ? "review_required" : "optional");
          if (!tierGatesProgression(tier)) return;
          const existing = requiredAssignmentsByModule.get(a.module_id) || [];
          existing.push({ id: a.id, completion_requirement: tier });
          requiredAssignmentsByModule.set(a.module_id, existing);
        });
      }

      // Build a map of assignment_id -> status for quick lookups
      const submissionStatusMap = new Map<string, AssignmentSubmissionStatus>();
      (submissions || []).forEach((s: any) => {
        if (s.assignment_id) {
          submissionStatusMap.set(s.assignment_id, s.status as AssignmentSubmissionStatus);
        }
      });

      // Transform submissions to match our interface
      const typedSubmissions: SubmissionWithContext[] = (submissions || [])
        .filter((s: any) => s.assignment?.module?.course)
        .map((s: any) => ({
          id: s.id,
          assignment_id: s.assignment_id,
          status: s.status as AssignmentSubmissionStatus,
          submitted_at: s.submitted_at,
          instructor_feedback: s.instructor_feedback,
          isLocked: false, // Will be calculated below
          lockReason: undefined,
          assignment: {
            id: s.assignment.id,
            title: s.assignment.title,
            description: s.assignment.description,
            required: s.assignment.required,
            completion_requirement: (s.assignment.completion_requirement as AssignmentCompletionRequirement)
              ?? (s.assignment.required ? "review_required" : "optional"),
            module_id: s.assignment.module_id,
            module: {
              id: s.assignment.module.id,
              title: s.assignment.module.title,
              sequence_order: s.assignment.module.sequence_order,
              course_id: s.assignment.module.course_id,
              course: {
                id: s.assignment.module.course.id,
                title: s.assignment.module.course.title,
                thumbnail_url: s.assignment.module.course.thumbnail_url,
              },
            },
          },
        }));

      // Check lock status for each submission (synchronously using pre-fetched data)
      for (const sub of typedSubmissions) {
        const courseId = sub.assignment.module.course_id;
        const modules = modulesByCourse.get(courseId) || [];
        const lockResult = isModuleLockedSync(
          sub.assignment.module.sequence_order,
          modules,
          requiredAssignmentsByModule,
          submissionStatusMap
        );
        sub.isLocked = lockResult.locked;
        sub.lockReason = lockResult.reason;
      }

      // Check lock status for each assignment (synchronously using pre-fetched data)
      for (const assignment of allAssignments) {
        const courseId = assignment.module.course_id;
        const modules = modulesByCourse.get(courseId) || [];
        const lockResult = isModuleLockedSync(
          assignment.module.sequence_order,
          modules,
          requiredAssignmentsByModule,
          submissionStatusMap
        );
        assignment.isLocked = lockResult.locked;
        assignment.lockReason = lockResult.reason;
      }

      // Categorize submissions
      const submittedIds = new Set(typedSubmissions.map((s) => s.assignment_id));
      
      const needsAttention = typedSubmissions.filter(
        (s) => s.status === "retry" || s.status === "failed"
      );
      const awaitingReview = typedSubmissions.filter((s) => s.status === "submitted");
      const completed = typedSubmissions.filter((s) => s.status === "passed");
      const notStarted = allAssignments.filter((a) => !submittedIds.has(a.id));

      return {
        needsAttention,
        awaitingReview,
        completed,
        notStarted,
        totalCount: typedSubmissions.length + notStarted.length,
        needsAttentionCount: needsAttention.length,
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent refetching
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });
}

// Separate hook for just the badge count (lighter weight)
export function useNeedsAttentionCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["needs_attention_count", user?.id],
    queryFn: async (): Promise<number> => {
      if (!user) return 0;

      const { count, error } = await supabase
        .from("assignment_submissions")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .in("status", ["retry", "failed"]);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user,
  });
}
